// Persistent KaziPeople player — a floating rounded banner near the bottom of the
// authenticated app, plus a right-side panel host (Now Playing / Queue / Lyrics). Mounted
// ONCE in the app shell and portaled to <body>, so it survives route changes without
// restarting playback. Self-hides unless Apple Music is connected (honest states only).
// Original KaziPeople design — inspired by, not copied from, the Apple layout reference.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { Shuffle, SkipBack, SkipForward, Play, Pause, Repeat, Repeat1, Quote, ListMusic, Volume2, MoreHorizontal, X, Maximize2, Music2, Loader2 } from 'lucide-react';
import { appleMusicProvider } from '../lib/appleMusicProvider';
import { usePlayer } from './usePlayer';
import { useSessionPersistence } from './useSessionPersistence';
import { NowPlaying } from './NowPlaying';
import { QueuePanel } from './QueuePanel';
import { LyricsPanel } from './LyricsPanel';

const provider = appleMusicProvider();
const CONNECTED = new Set(['connected_eligible', 'connected_no_subscription']);

export const PersistentPlayer: React.FC = () => {
  const p = usePlayer();
  const { snap, ui } = p;

  useEffect(() => { void p.refreshAuth(); /* once on mount */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A page reload kills the MusicKit queue; this brings it back (paused, at the same spot).
  useSessionPersistence(p);

  // Page gate: the visible bar only appears on Dashboard, Conversations and Music — the
  // player stays MOUNTED everywhere (audio keeps playing in the background), we just hide
  // the chrome elsewhere so it isn't "too much". `*` is the child path under TenantLayout;
  // '' is the dashboard index. Playback is owned by the provider, not this component, so
  // returning null never stops the music.
  const params = useParams();
  const sub = (params['*'] || '').replace(/^\/+/, '').split('/')[0];
  const onPlayerPage = sub === '' || sub === 'dashboard' || sub === 'messages' || sub === 'music';

  // Honest gate: only show when a real connection exists (or something is already playing).
  const visible = onPlayerPage && (CONNECTED.has(ui.authState) || !!snap.item);

  // Publish the bar's height so the content area (main) can reserve scroll padding — the page keeps
  // its layout constant (chrome doesn't move); content scrolls ABOVE the fixed bar. Cleared on hide.
  useEffect(() => {
    document.documentElement.style.setProperty('--kp-player-h', visible ? '64px' : '0px');
    return () => document.documentElement.style.setProperty('--kp-player-h', '0px');
  }, [visible]);

  // Escape always closes the side panel — a reliable exit even if a layer traps the X.
  useEffect(() => {
    if (ui.panel === 'none') return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') p.openPanel('none'); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ui.panel]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  const item = snap.item;
  const playing = snap.status === 'playing';
  const busy = snap.status === 'loading' || snap.status === 'buffering';
  const artwork = provider.artworkUrl(item?.artwork, 96);
  const pct = snap.durationMs ? (snap.positionMs / snap.durationMs) * 100 : 0;
  const nextRepeat = snap.repeat === 'off' ? 'all' : snap.repeat === 'all' ? 'one' : 'off';

  const panelTitle = ui.panel === 'lyrics' ? 'Lyrics' : ui.panel === 'queue' ? 'Queue' : 'Now Playing';

  return (
    <>
      {/* Right-side panel (Now Playing / Queue / Lyrics) — a fixed overlay, portaled to body. */}
      {ui.panel !== 'none' && createPortal(
        <aside
          role="dialog" aria-label={panelTitle}
          // Sits BELOW the sticky app header (z-50) and ABOVE the bottom bar. z-[60] so its
          // close button is never trapped under the header (the "can't close it" bug).
          className="fixed right-0 top-16 bottom-16 z-[60] flex w-full flex-col border-l border-border bg-card p-4 shadow-xl sm:w-[380px]"
        >
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold text-foreground">{panelTitle}</h2>
            <button type="button" aria-label="Close panel" onClick={() => p.openPanel('none')}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="min-h-0 flex-1">
            {ui.panel === 'nowplaying' && <NowPlaying />}
            {ui.panel === 'queue' && <QueuePanel />}
            {ui.panel === 'lyrics' && <LyricsPanel />}
          </div>
        </aside>,
        document.body,
      )}

      {/* Fixed bottom bar, pinned to the content area (absolute within the relative content column).
          The layout stays constant — main reserves --kp-player-h as scroll padding and its content
          scrolls ABOVE this bar. Ends ~72px short of the right so it never overlaps the Dana bubble. */}
      <div className="absolute inset-x-0 bottom-0 z-30 flex h-16 items-center gap-2 border-t border-border bg-card px-3 pr-[72px]">
          {/* transport */}
          <div className="flex items-center gap-0.5">
            <Ctl label="Shuffle" active={snap.shuffle} onClick={() => p.shuffle(!snap.shuffle)}><Shuffle className="h-4 w-4" /></Ctl>
            <Ctl label="Previous" onClick={p.prev}><SkipBack className="h-5 w-5" /></Ctl>
            <button type="button" aria-label={playing ? 'Pause' : 'Play'} onClick={p.toggle}
              className="mx-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-white transition hover:bg-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 translate-x-0.5" />}
            </button>
            <Ctl label="Next" onClick={p.next}><SkipForward className="h-5 w-5" /></Ctl>
            <Ctl label={`Repeat: ${snap.repeat}`} active={snap.repeat !== 'off'} onClick={() => p.repeat(nextRepeat)}>
              {snap.repeat === 'one' ? <Repeat1 className="h-4 w-4" /> : <Repeat className="h-4 w-4" />}
            </Ctl>
          </div>

          {/* metadata + progress — click opens Now Playing */}
          <button type="button" onClick={() => p.openPanel('nowplaying')} aria-label="Open Now Playing"
            className="mx-1 flex min-w-0 flex-1 items-center gap-3 rounded-lg px-1 py-1 text-left hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
            {artwork ? <img src={artwork} alt="" className="h-10 w-10 shrink-0 rounded object-cover" />
              : <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-muted"><Music2 className="h-4 w-4 text-muted-foreground" /></div>}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-foreground" dir="auto">{item?.title || 'Not playing'}</div>
              <div className="truncate text-xs text-muted-foreground" dir="auto">{item?.artist || (ui.authState === 'connected_no_subscription' ? 'Connected — no active subscription' : 'Apple Music connected')}</div>
              <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary-500 transition-[width]" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </button>

          {/* right actions */}
          <div className="flex items-center gap-0.5">
            <Ctl label="Lyrics" active={ui.panel === 'lyrics'} onClick={() => p.togglePanel('lyrics')}><Quote className="h-[18px] w-[18px]" /></Ctl>
            <Ctl label="Queue" active={ui.panel === 'queue'} onClick={() => p.togglePanel('queue')}><ListMusic className="h-[18px] w-[18px]" /></Ctl>
            <Ctl label="Volume" onClick={() => p.volume(snap.status === 'playing' ? 1 : 1)}><Volume2 className="h-[18px] w-[18px]" /></Ctl>
            <Ctl label="Open Now Playing" onClick={() => p.openPanel('nowplaying')}><Maximize2 className="hidden h-[18px] w-[18px] sm:block" /></Ctl>
            <Ctl label="More" onClick={() => p.openPanel('nowplaying')}><MoreHorizontal className="h-[18px] w-[18px]" /></Ctl>
          </div>
      </div>
    </>
  );
};

const Ctl: React.FC<{ label: string; active?: boolean; onClick: () => void; children: React.ReactNode }> = ({ label, active, onClick, children }) => (
  <button type="button" aria-label={label} title={label} aria-pressed={active} onClick={onClick}
    className={`rounded-full p-1.5 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${active ? 'text-primary-500' : 'text-muted-foreground hover:text-foreground'}`}>
    {children}
  </button>
);
